/**
 * ApiKeyList Component
 *
 * Displays a list of API keys with status indicators, actions for
 * enable/disable/delete, and inline usage preview.
 */

import React, { useCallback } from 'react';
import { clsx } from 'clsx';
import { StatusBadge } from '../shared/StatusBadge';
import type { ApiKeyStatusItem } from '../../../services/llmAdminApi';

export interface ApiKeyListProps {
  keys: ApiKeyStatusItem[];
  onDisable?: (keyIndex: number) => void;
  onEnable?: (keyIndex: number) => void;
  onDelete?: (keyIndex: number) => void;
  onViewUsage?: (keyIndex: number) => void;
  disabled?: boolean;
}

const statusVariantMap: Record<string, 'success' | 'error' | 'warning'> = {
  enabled: 'success',
  disabled: 'error',
  exhausted: 'warning',
};

const statusLabelMap: Record<string, string> = {
  enabled: '正常',
  disabled: '已禁用',
  exhausted: '已耗尽',
};

export const ApiKeyList: React.FC<ApiKeyListProps> = ({
  keys,
  onDisable,
  onEnable,
  onDelete,
  onViewUsage,
  disabled = false,
}) => {
  const handleAction = useCallback(
    (e: React.MouseEvent, action: () => void) => {
      e.stopPropagation();
      action();
    },
    []
  );

  if (keys.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm border border-dashed border-gray-300 rounded-lg">
        暂无 API Key，点击上方按钮添加
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {keys.map((key) => (
        <div
          key={key.key_index}
          className={clsx(
            'flex items-center justify-between px-4 py-3 rounded-lg border',
            key.status === 'enabled'
              ? 'bg-white border-gray-200'
              : 'bg-gray-50 border-gray-300'
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={clsx(
                'w-2.5 h-2.5 rounded-full flex-shrink-0',
                key.status === 'enabled' && 'bg-green-500',
                key.status === 'disabled' && 'bg-red-500',
                key.status === 'exhausted' && 'bg-yellow-500'
              )}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-gray-800 truncate">
                  {key.key_preview}
                </span>
                <StatusBadge
                  variant={statusVariantMap[key.status] || 'neutral'}
                  size="sm"
                >
                  {statusLabelMap[key.status] || key.status}
                </StatusBadge>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                <span>已用配额: {key.used_quota}</span>
                {key.error_count > 0 && (
                  <span className="text-red-500">
                    错误: {key.error_count}
                  </span>
                )}
                {key.disabled_reason && (
                  <span className="text-gray-400 truncate">
                    原因: {key.disabled_reason}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0 ml-3">
            {onViewUsage && (
              <button
                type="button"
                onClick={(e) => handleAction(e, () => onViewUsage(key.key_index))}
                disabled={disabled}
                className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors disabled:opacity-50"
                title="查看使用情况"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </button>
            )}
            {key.status === 'enabled' && onDisable && (
              <button
                type="button"
                onClick={(e) => handleAction(e, () => onDisable(key.key_index))}
                disabled={disabled}
                className="p-1.5 text-gray-400 hover:text-yellow-600 transition-colors disabled:opacity-50"
                title="禁用"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </button>
            )}
            {key.status !== 'enabled' && onEnable && (
              <button
                type="button"
                onClick={(e) => handleAction(e, () => onEnable(key.key_index))}
                disabled={disabled}
                className="p-1.5 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                title="启用"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={(e) => handleAction(e, () => onDelete(key.key_index))}
                disabled={disabled || keys.length <= 1}
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                title={keys.length <= 1 ? '至少保留一个 Key' : '删除'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ApiKeyList;
